import { useState } from "react";
import { LuBookOpen, LuChartColumn, LuCrown, LuFlame, LuStar, LuTrendingUp, LuUsers } from "react-icons/lu";
import { get } from "../api";
import { SUBJECTS } from "../../shared/constants";
import { Avatar } from "../components/Avatar";
import { Count, ErrorBox, Spinner } from "../components/ui";
import { HeroBubble, PageHero } from "../components/PageHero";
import { Link } from "../router";
import { useApp, useLoad } from "../store";

const SCOPES = [
  { id: "weekly", name: "Ugens XP", icon: LuFlame },
  { id: "all", name: "Samlet XP", icon: LuStar },
  { id: "subject", name: "Pr. fag", icon: LuBookOpen },
  { id: "class", name: "Min klasse", icon: LuUsers },
] as const;

/** the three best on a real 3D podium (CSS 3D boxes): the figures stand on top, the blocks rise one after the other */
function Podium({ top }: { top: any[] }) {
  const order = [top[1], top[0], top[2]].map((e, i) => ({ e, place: [2, 1, 3][i]! })).filter((x) => x.e);
  return (
    <div className="podium" role="group" aria-label="De tre bedste" data-tilt="5">
      {order.map(({ e, place }) => (
        <div key={e.rank} className={`pd p${place}${e.you ? " you" : ""}`} style={{ ["--i" as any]: place }}>
          <div className="pd-who">
            {place === 1 && <LuCrown className="pd-crown" aria-hidden="true" size={34} />}
            <Avatar equipped={e.avatar} size={place === 1 ? 104 : 84} label="" />
            <strong>{e.username}{e.you ? " (dig)" : ""}</strong>
            <small><Count to={e.score} /> XP</small>
          </div>
          <div className="pd-box" aria-hidden="true">
            <i className="f" /><i className="r" /><i className="t" />
            <b>{place}</b>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Leaderboard() {
  const { me } = useApp();
  const [scope, setScope] = useState("weekly");
  const [subject, setSubject] = useState("matematik");
  const [classId, setClassId] = useState<number | null>(null);
  const classes = useLoad(() => get<{ classes: any[] }>("/api/classes/mine"), []);
  const q = scope === "subject" ? `&subject=${subject}` : scope === "class" ? `&classId=${classId ?? 0}` : "";
  const board = useLoad(() => (scope === "class" && !classId ? Promise.resolve(null as any) : get<any>(`/api/leaderboard?scope=${scope}${q}`)), [scope, subject, classId]);
  const myClasses = classes.data?.classes ?? [];

  const entries: any[] = board.data?.entries ?? [];
  const mine = entries.find((e) => e.you);
  const above = mine && mine.rank > 1 ? entries.find((e) => e.rank === mine.rank - 1) : null;
  const gap = above && mine ? Math.max(0, above.score - mine.score) : 0;
  const best = Math.max(1, ...entries.map((e) => e.score));

  return (
    <div className="stack ranking">
      <PageHero
        tone="rank"
        title="Rangliste"
        icon={<LuChartColumn size={44} />}
        sub={<>Kun brugernavn og figur vises. <Link to="/profil">Skjul mig</Link></>}
      >
        {mine ? (
          <HeroBubble icon={<LuTrendingUp aria-hidden="true" size={22} />} tone="gold" value={<>Nr. {mine.rank}</>} label={`${mine.score} XP`} />
        ) : (
          <HeroBubble icon={<LuCrown aria-hidden="true" size={22} />} tone="violet" value="Kom på listen" label="spil et spil" />
        )}
      </PageHero>

      <div className="tabs filters" role="tablist" aria-label="Ranglistetype">
        {SCOPES.map((s) => (
          <button key={s.id} type="button" role="tab" aria-selected={scope === s.id} className={`fchip${scope === s.id ? " on" : ""}`} onClick={() => { setScope(s.id); if (s.id === "class" && !classId && myClasses[0]) setClassId(myClasses[0].id); }}>
            <s.icon aria-hidden="true" size={20} /> {s.name}
          </button>
        ))}
      </div>
      {scope === "subject" && (
        <label className="field">Fag<select value={subject} onChange={(e: any) => setSubject(e.target.value)}>{SUBJECTS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
      )}
      {scope === "class" && (myClasses.length === 0 ? <p className="notice">Du er ikke med i en klasse endnu. <Link to="/klasse">Tilmeld dig med en klassekode</Link>.</p> : (
        <label className="field">Klasse<select value={classId ?? ""} onChange={(e: any) => setClassId(Number(e.target.value))}>{myClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
      ))}

      {board.loading && !board.data ? <Spinner /> : board.error ? <ErrorBox error={board.error} retry={board.reload} /> : board.data && (
        <div className="card lb-card">
          <h2 className="sec-title">{board.data.label}</h2>
          {board.data.you && !board.data.you.visible && me?.role === "student" && <div className="notice">Du er skjult. Ændr det under Profil.</div>}
          {entries.length === 0 ? <p className="muted">Ingen på listen endnu – spil og kom på!</p> : (
            <>
              {mine && (
                <div className="cheer inline lb-you" role="status">
                  <LuTrendingUp aria-hidden="true" size={24} />
                  <span>{mine.rank === 1 ? <><b>Du fører listen!</b></> : <><b>Du er nr. {mine.rank}.</b> Kun <b>{gap} XP</b> op til nr. {mine.rank - 1}.</>}</span>
                </div>
              )}
              <Podium top={entries.slice(0, 3)} />
              {entries.length > 3 && (
                <ol className="lb" start={4}>
                  {entries.slice(3).map((e: any, i: number) => (
                    <li key={e.rank} className={e.you ? "me" : ""} style={{ ["--i" as any]: Math.min(i, 12), ["--w" as any]: `${Math.max(4, Math.round((100 * e.score) / best))}%` }}>
                      <span className="rank">{e.rank}</span>
                      <Avatar equipped={e.avatar} size={46} label="" />
                      <span className="who"><strong>{e.username}{e.you ? " (dig)" : ""}</strong><small className="muted">Niveau {e.level}{e.title ? ` · ${e.title}` : ""}</small></span>
                      <strong className="score">{e.score}</strong>
                      <i className="bar" aria-hidden="true" />
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
